// Fonts bundled with the app for the Content Studio, all SIL Open Font License 1.1
// (licences in node_modules/@fontsource/*/LICENSE). Canvas text uses only faces registered here,
// so the preview and the export draw with the same files; anything else is a "missing font".
import inter400 from '@fontsource/inter/files/inter-latin-400-normal.woff2?url';
import inter600 from '@fontsource/inter/files/inter-latin-600-normal.woff2?url';
import inter700 from '@fontsource/inter/files/inter-latin-700-normal.woff2?url';
import playfair400 from '@fontsource/playfair-display/files/playfair-display-latin-400-normal.woff2?url';
import playfair700 from '@fontsource/playfair-display/files/playfair-display-latin-700-normal.woff2?url';

const FACES: { family: string; weight: number; url: string }[] = [
  { family: 'Inter', weight: 400, url: inter400 },
  { family: 'Inter', weight: 600, url: inter600 },
  { family: 'Inter', weight: 700, url: inter700 },
  { family: 'Playfair Display', weight: 400, url: playfair400 },
  { family: 'Playfair Display', weight: 700, url: playfair700 },
];

export const BUNDLED_FAMILIES = Array.from(new Set(FACES.map((f) => f.family)));
const loaded = new Set<string>();
let pending: Promise<void> | null = null;

/** Loads every bundled face once. Resolves when all are usable by canvas. */
export function ensureFonts(): Promise<void> {
  if (!pending) {
    pending = Promise.all(
      FACES.map(async (f) => {
        const face = new FontFace(f.family, `url(${f.url})`, { weight: String(f.weight), style: 'normal' });
        await face.load();
        document.fonts.add(face);
        loaded.add(`${f.family}|${f.weight}`);
      }),
    ).then(() => undefined);
  }
  return pending;
}

export function familyAvailable(family: string): boolean {
  return FACES.some((f) => f.family === family && loaded.has(`${f.family}|${f.weight}`));
}

/** The bundled weight closest to the one asked for (canvas would otherwise synthesise bold). */
export function nearestWeight(family: string, weight: number): number {
  const ws = FACES.filter((f) => f.family === family).map((f) => f.weight);
  if (!ws.length) return weight;
  return ws.reduce((a, b) => (Math.abs(b - weight) < Math.abs(a - weight) ? b : a));
}
